import { PaperAirplaneIcon } from "@heroicons/react/24/outline";
import React, { useState } from "react";

import { Input } from "@fronterahealth/frontera-ui-components";

import {
  ApiMediaCommentsCommentTypeChoices,
  MediaCommentEnum,
  MediaCommentsType,
  useCreateMediaCommentMutation,
} from "@api/graphql/types-and-hooks";
import { convertMediaCommentToComment } from "@pages/VideoSessionsV2/components/Comments/helper";
import { useVideoSessionData } from "@providers/VideoSessionProviderV2";

interface CommentFormProps {
  parentId: string;
  mediaStartTime: number;
  commentType: ApiMediaCommentsCommentTypeChoices | undefined;
  handleCommentSubmit: () => void;
}
const CommentForm: React.FC<CommentFormProps> = ({ parentId, mediaStartTime, commentType, handleCommentSubmit }) => {
  const [comment, setComment] = useState<string>("");
  const { selectedMedia, setAllFilteredComments } = useVideoSessionData();
  const createCommentMutation = useCreateMediaCommentMutation();

  // Handle comment input change
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setComment(event.target.value);
  };

  // Handle form submission (for example, just log the comment)
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (comment.trim()) {
      await createCommentMutation
        .mutateAsync({
          input: {
            content: comment.trim(),
            commentType: commentType as unknown as MediaCommentEnum,
            mediaId: selectedMedia?.id || "",
            mediaStartTime: mediaStartTime || 0,
            parentCommentId: parentId,
          },
        })
        .then((data) => {
          if (data.createMediaComment?.status) {
            handleCommentSubmit();
            setAllFilteredComments((prevComments) => {
              const newComment = convertMediaCommentToComment(
                data.createMediaComment?.mediaComment as MediaCommentsType,
              );

              if (!newComment.parentCommentID) return prevComments;

              return prevComments.map((comment) => {
                if (comment.id === newComment.parentCommentID) {
                  return {
                    ...comment,
                    lastReply: [...(comment.lastReply ?? []), newComment],
                    totalReplies: (comment.totalReplies ?? 0) + 1,
                  };
                }
                return comment;
              });
            });
          }
        });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="relative w-full">
      <Input
        label=""
        name="comment"
        type="text"
        value={comment}
        onChange={handleChange}
        placeholder="Leave your comment..."
        className="w-full"
        inputSize="full"
        formNoValidate
      />
      <button
        className="absolute top-[14px] right-[5px]"
        disabled={!comment.trim() || createCommentMutation.isPending} // Disable if input is empty
      >
        {createCommentMutation.isPending ? (
          <div className="animate-spin rounded-full h-5 w-5 border-b border-limestone-900" />
        ) : (
          <PaperAirplaneIcon className="h-6 w-6 text-limestone-600 bg-white" />
        )}
      </button>
    </form>
  );
};

export default CommentForm;
